import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Activity, Phone, MapPin, MessageSquare, MessageCircle, Users,
  HandCoins, CalendarClock, Calendar, Trophy, Download, RefreshCcw,
  Filter, ArrowRightLeft,
} from 'lucide-react';
import { OwnerLayout } from '../../../components/OwnerLayout';
import api, { encodeLan } from '../../../api';

// ---------------------------- helpers ---------------------------------
function fmtINR(n) {
  const num = Number(n || 0);
  if (num >= 10000000) return `\u20B9${(num / 10000000).toFixed(2)} Cr`;
  if (num >= 100000) return `\u20B9${(num / 100000).toFixed(2)} L`;
  if (num >= 1000) return `\u20B9${(num / 1000).toFixed(1)} K`;
  return `\u20B9${num.toFixed(0)}`;
}

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'last30', label: 'Last 30 days' },
  { key: 'custom', label: 'Custom' },
];

function presetRange(key) {
  const now = new Date();
  if (key === 'today') return { from: ymd(now), to: ymd(now) };
  if (key === 'yesterday') {
    const y = new Date(now); y.setDate(now.getDate() - 1);
    return { from: ymd(y), to: ymd(y) };
  }
  if (key === 'week') {
    const day = now.getDay() || 7;
    const monday = new Date(now); monday.setDate(now.getDate() - (day - 1));
    return { from: ymd(monday), to: ymd(now) };
  }
  if (key === 'month') {
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: ymd(first), to: ymd(now) };
  }
  if (key === 'last30') {
    const from = new Date(now); from.setDate(now.getDate() - 29);
    return { from: ymd(from), to: ymd(now) };
  }
  return { from: ymd(now), to: ymd(now) };
}

// Group meta — label, colour, icon. 'contact' is the meta-group covering
// every non-visit contact (calls, sms, whatsapp, meetings) for the chip bar.
const GROUP_META = {
  call:          { label: 'Call',          icon: Phone,           color: '#1d4ed8', bg: '#dbeafe' },
  visit:         { label: 'Field Visit',   icon: MapPin,          color: '#16a34a', bg: '#dcfce7' },
  sms:           { label: 'SMS',           icon: MessageSquare,   color: '#0891b2', bg: '#cffafe' },
  whatsapp:      { label: 'WhatsApp',      icon: MessageCircle,   color: '#15803d', bg: '#d1fae5' },
  meeting:       { label: 'Meeting',       icon: Users,           color: '#7c3aed', bg: '#ede9fe' },
  status_change: { label: 'Status Change', icon: ArrowRightLeft,  color: '#475569', bg: '#e2e8f0' },
  ptp:           { label: 'PTP',           icon: CalendarClock,   color: '#b45309', bg: '#fef3c7' },
  payment:       { label: 'Payment',       icon: HandCoins,       color: '#16a34a', bg: '#dcfce7' },
};

function groupMeta(g) {
  return GROUP_META[g] || { label: g || 'Activity', icon: Activity, color: '#64748b', bg: '#f1f5f9' };
}

const TYPE_FILTERS = [
  { value: '',              label: 'All Activity' },
  { value: 'call',          label: 'Calls' },
  { value: 'visit',         label: 'Visits' },
  { value: 'ptp',           label: 'PTPs' },
  { value: 'payment',       label: 'Payments' },
  { value: 'contact',       label: 'SMS / WhatsApp / Meeting' },
  { value: 'status_change', label: 'Status changes' },
];

// Friendly labels for recovery case statuses (matches owner/recovery/Dashboard).
const STATUS_LABELS = {
  new_overdue: 'New Overdue',
  contact_attempted: 'Contact Attempted',
  connected: 'Connected',
  ptp_set: 'PTP Set',
  partially_paid: 'Partially Paid',
  regularized: 'Regularized',
  settled_closed: 'Settled / Closed',
  dispute_hold: 'Dispute Hold',
  escalated_supervisor: 'Escalated',
  legal_initiated: 'Legal Initiated',
  written_off: 'Written Off',
};
const statusLabel = (s) => STATUS_LABELS[s] || (s || '').replace(/_/g, ' ');

// ---------------------------- component -------------------------------
export default function RecoveryActivity() {
  const navigate = useNavigate();
  const loc = useLocation();

  // Defaults from URL — lets external links deep-link into a pre-filtered
  // view (e.g. Team 360 \u2192 a specific agent's payments for a date range).
  const initialQs = useMemo(() => {
    const sp = new URLSearchParams(loc.search);
    return {
      type: sp.get('type') || '',
      agentId: sp.get('agentId') || '',
      branchId: sp.get('branchId') || '',
      from: sp.get('from') || '',
      to: sp.get('to') || '',
    };
  }, [loc.search]);

  const hasCustomRange = Boolean(initialQs.from || initialQs.to);
  const [preset, setPreset] = useState(hasCustomRange ? 'custom' : 'today');
  const [range, setRange] = useState(hasCustomRange
    ? { from: initialQs.from || ymd(new Date()), to: initialQs.to || ymd(new Date()) }
    : presetRange('today'));
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(initialQs.branchId);
  const [agentId, setAgentId] = useState(initialQs.agentId);
  const [typeFilter, setTypeFilter] = useState(initialQs.type);
  const [data, setData] = useState({ items: [], summary: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/branches').then((r) => setBranches(r.data || [])).catch(() => {});
  }, []);

  const applyPreset = (key) => {
    setPreset(key);
    if (key !== 'custom') setRange(presetRange(key));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 3000 };
      if (range.from) params.from = range.from;
      if (range.to) params.to = range.to;
      if (branchId) params.branchId = branchId;
      if (agentId) params.agentId = agentId;
      if (typeFilter) params.type = typeFilter;
      const res = await api.get('/recovery/activity', { params });
      setData(res.data || { items: [], summary: null });
    } catch (err) {
      console.error(err);
      setData({ items: [], summary: null });
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, branchId, agentId, typeFilter]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = () => {
    const p = new URLSearchParams();
    if (range.from) p.set('from', range.from);
    if (range.to) p.set('to', range.to);
    if (branchId) p.set('branchId', branchId);
    if (agentId) p.set('agentId', agentId);
    if (typeFilter) p.set('type', typeFilter);
    p.set('format', 'csv');
    api.get(`/recovery/activity?${p.toString()}`, { responseType: 'blob' }).then((res) => {
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `recovery_activity_${range.from || 'all'}_${range.to || 'all'}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    }).catch((e) => console.error(e));
  };

  const summary = useMemo(
    () => data.summary || {
      totalActivities: 0, totalCollected: 0, totalPromised: 0,
      uniqueCustomers: 0, uniqueAgents: 0,
      byType: [], byAgent: [], byDay: [],
    },
    [data.summary]
  );
  const items = useMemo(() => data.items || [], [data.items]);

  // Counts by group for KPI tiles (always show 0 if absent)
  const countByGroup = useMemo(() => {
    const map = {};
    (summary.byType || []).forEach((t) => { map[t.group] = t; });
    return map;
  }, [summary.byType]);
  const getCount = (g) => countByGroup[g]?.count || 0;
  const getAmount = (g) => countByGroup[g]?.amount || 0;

  // Agent dropdown built from response
  const agentOptions = useMemo(() => {
    const seen = new Map();
    items.forEach((i) => {
      if (i.performedByUserId && !seen.has(i.performedByUserId)) {
        seen.set(i.performedByUserId, { id: i.performedByUserId, name: i.performedByName || i.performedByUserId });
      }
    });
    summary.byAgent?.forEach((a) => {
      if (a.userId && !seen.has(a.userId)) seen.set(a.userId, { id: a.userId, name: a.name });
    });
    return Array.from(seen.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [items, summary.byAgent]);

  const maxDayCount = Math.max(1, ...(summary.byDay || []).map((d) => d.count));

  return (
    <OwnerLayout title="Recovery Activity">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading font-bold text-navy text-lg">Recovery Activity</h2>
          <p className="text-sm text-gray">Every call, visit, PTP and payment logged by your recovery team</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn-outline" onClick={load} title="Refresh">
            <RefreshCcw size={14} style={{ marginRight: '4px' }} /> Refresh
          </button>
          <button className="btn-navy" onClick={downloadCsv} disabled={!items.length}>
            <Download size={14} style={{ marginRight: '4px' }} /> Export CSV
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="panel" style={{ padding: '12px 16px', marginBottom: '12px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: 'var(--g3)', fontWeight: 600 }}>
            <Filter size={12} /> Range:
          </span>
          {PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => applyPreset(p.key)}
              className={preset === p.key ? 'btn-navy' : 'btn-outline'}
              style={{ padding: '4px 10px', fontSize: '12px', minWidth: 'auto' }}
              data-testid={`act-preset-${p.key}`}
            >
              {p.label}
            </button>
          ))}
          {preset === 'custom' && (
            <>
              <input
                type="date"
                className="form-input"
                style={{ width: 'auto', fontSize: '12px', padding: '4px 8px' }}
                value={range.from}
                onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
              />
              <span>→</span>
              <input
                type="date"
                className="form-input"
                style={{ width: 'auto', fontSize: '12px', padding: '4px 8px' }}
                value={range.to}
                onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
              />
            </>
          )}
          <div style={{ width: '1px', height: '20px', background: 'var(--g2)', margin: '0 4px' }} />
          {branches.length > 1 && (
            <select
              className="form-input"
              style={{ width: 'auto', fontSize: '12px', padding: '4px 8px' }}
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
            >
              <option value="">All branches</option>
              {branches.map((b) => (<option key={b.id} value={b.id}>{b.branchName}</option>))}
            </select>
          )}
          <select
            className="form-input"
            style={{ width: 'auto', fontSize: '12px', padding: '4px 8px' }}
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
          >
            <option value="">All agents</option>
            {agentOptions.map((a) => (<option key={a.id} value={a.id}>{a.name}</option>))}
          </select>
          <select
            className="form-input"
            style={{ width: 'auto', fontSize: '12px', padding: '4px 8px' }}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            {TYPE_FILTERS.map((t) => (
              <option key={t.value || 'all'} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* KPI tiles — count of each major activity type */}
      <div className="od-stats">
        <div className="od-stat" style={{ borderLeft: '3px solid #1d4ed8' }}>
          <div className="od-stat-lbl"><Phone size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Calls</div>
          <div className="od-stat-val" style={{ color: '#1d4ed8' }}>{getCount('call')}</div>
          <div className="od-stat-sub">Logged in range</div>
        </div>
        <div className="od-stat" style={{ borderLeft: '3px solid #16a34a' }}>
          <div className="od-stat-lbl"><MapPin size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Field Visits</div>
          <div className="od-stat-val" style={{ color: '#16a34a' }}>{getCount('visit')}</div>
          <div className="od-stat-sub">On-the-ground</div>
        </div>
        <div className="od-stat" style={{ borderLeft: '3px solid #b45309' }}>
          <div className="od-stat-lbl"><CalendarClock size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />PTPs Recorded</div>
          <div className="od-stat-val" style={{ color: '#b45309' }}>{getCount('ptp')}</div>
          <div className="od-stat-sub">{fmtINR(getAmount('ptp'))} promised</div>
        </div>
        <div className="od-stat" style={{ borderLeft: '3px solid #15803d' }}>
          <div className="od-stat-lbl"><HandCoins size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Payments</div>
          <div className="od-stat-val" style={{ color: '#15803d' }}>{fmtINR(summary.totalCollected)}</div>
          <div className="od-stat-sub">{getCount('payment')} collection{getCount('payment') !== 1 ? 's' : ''}</div>
        </div>
      </div>

      {/* Secondary chip row — every group with at least one entry */}
      {summary.byType?.length > 0 && (
        <div className="panel" style={{ marginTop: '12px', padding: '12px 16px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', color: 'var(--g3)', fontWeight: 600 }}>Breakdown:</span>
            {summary.byType.map((t) => {
              const meta = groupMeta(t.group);
              const Icon = meta.icon;
              const active = typeFilter === t.group;
              return (
                <button
                  key={t.group}
                  type="button"
                  onClick={() => setTypeFilter(active ? '' : t.group)}
                  title={active ? 'Clear filter' : `Filter to ${meta.label}`}
                  style={{
                    fontSize: '12px',
                    padding: '4px 12px',
                    background: active ? meta.color : meta.bg,
                    color: active ? '#fff' : meta.color,
                    borderRadius: '999px',
                    border: `1px solid ${meta.color}`,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontWeight: 600,
                  }}
                >
                  <Icon size={12} />
                  <span>{meta.label}</span>
                  <span>·</span>
                  <span>{t.count}</span>
                  {t.amount > 0 && (<><span>·</span><span>{fmtINR(t.amount)}</span></>)}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Daily trend */}
      {(summary.byDay?.length || 0) > 1 && (
        <div className="panel" style={{ marginTop: '12px' }}>
          <div className="panel-header">
            <span className="panel-title"><Calendar size={14} /> Daily Activity Trend</span>
          </div>
          <div style={{ padding: '16px', display: 'flex', alignItems: 'flex-end', gap: '6px', minHeight: '140px' }}>
            {summary.byDay.map((d) => (
              <div key={d.date} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '40px' }}>
                <div style={{ fontSize: '10px', color: 'var(--g3)', marginBottom: '2px' }}>{d.count}</div>
                <div
                  title={`${d.date}: ${d.count} activities · ${fmtINR(d.collected)} collected`}
                  style={{
                    width: '100%',
                    background: 'var(--navy, #1e3a8a)',
                    height: `${Math.max(4, (d.count / maxDayCount) * 100)}px`,
                    borderRadius: '4px 4px 0 0',
                  }}
                />
                <div style={{ fontSize: '10px', color: 'var(--g3)', marginTop: '4px', whiteSpace: 'nowrap' }}>
                  {new Date(d.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Agent leaderboard — counts per activity type */}
      {(summary.byAgent?.length || 0) > 0 && (
        <div className="panel" style={{ marginTop: '12px' }}>
          <div className="panel-header">
            <span className="panel-title"><Trophy size={14} /> Agent Leaderboard</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: 'var(--g1)', borderBottom: '1px solid var(--g2)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)', width: '40px' }}>#</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>Agent</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>Calls</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>Visits</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>PTPs</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>Payments</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>Collected</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {summary.byAgent.map((a, i) => (
                  <tr
                    key={a.userId}
                    style={{ borderBottom: '1px solid var(--g2)', cursor: 'pointer' }}
                    onClick={() => setAgentId(a.userId === agentId ? '' : a.userId)}
                  >
                    <td style={{ padding: '8px 12px', color: 'var(--g3)' }}>{i + 1}</td>
                    <td style={{ padding: '8px 12px', fontWeight: 600, color: 'var(--navy)' }}>
                      {a.name}{a.employeeId ? ` (${a.employeeId})` : ''}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{a.calls || 0}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{a.visits || 0}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{a.ptps || 0}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{a.payments || 0}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      <strong style={{ color: 'var(--green, #16a34a)' }}>{fmtINR(a.collected || 0)}</strong>
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700 }}>{a.total || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Activity feed */}
      <div className="panel" style={{ marginTop: '12px' }}>
        <div className="panel-header">
          <span className="panel-title">
            <Activity size={14} /> Activity Feed ({items.length})
          </span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          {loading ? (
            <div style={{ padding: '32px', textAlign: 'center', color: 'var(--g3)' }}>Loading…</div>
          ) : items.length === 0 ? (
            <div style={{ padding: '32px', textAlign: 'center', color: 'var(--g3)' }}>No activity in this range.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: 'var(--g1)', borderBottom: '1px solid var(--g2)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>When</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>Activity</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>Customer</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>Loan</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>Agent</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>Branch</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>Detail</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: 'var(--g3)' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const meta = groupMeta(it.group);
                  const Icon = meta.icon;
                  const when = it.at ? new Date(it.at) : null;
                  return (
                    <tr
                      key={`${it.kind}-${it.id}`}
                      style={{ borderBottom: '1px solid var(--g2)', cursor: it.loanAccountNumber ? 'pointer' : 'default' }}
                      onClick={() => it.loanAccountNumber && navigate(`/owner/recovery/cases/${encodeLan(it.loanAccountNumber)}`)}
                    >
                      <td style={{ padding: '8px 12px', color: 'var(--g3)', whiteSpace: 'nowrap' }}>
                        {when ? (
                          <>
                            <div>{when.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' })}</div>
                            <div style={{ fontSize: '10px' }}>{when.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })}</div>
                          </>
                        ) : '—'}
                      </td>
                      <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 10px',
                          background: meta.bg,
                          color: meta.color,
                          borderRadius: '999px',
                          fontSize: '11px',
                          fontWeight: 600,
                        }}>
                          <Icon size={11} /> {meta.label}
                        </span>
                      </td>
                      <td style={{ padding: '8px 12px', minWidth: '160px' }}>
                        <div style={{ fontWeight: 600 }}>{it.customerName || it.clientId || 'Unknown'}</div>
                        {it.customerPhone && <div style={{ fontSize: '11px', color: 'var(--g3)' }}>{it.customerPhone}</div>}
                      </td>
                      <td style={{ padding: '8px 12px', color: 'var(--g3)', fontSize: '12px' }}>
                        <div>{it.loanAccountNumber || '—'}</div>
                        {it.schemeName && <div style={{ fontSize: '10px' }}>{it.schemeName}</div>}
                      </td>
                      <td style={{ padding: '8px 12px', fontSize: '12px' }}>{it.performedByName || '—'}</td>
                      <td style={{ padding: '8px 12px', fontSize: '12px', color: 'var(--g3)' }}>{it.branchName || '—'}</td>
                      <td style={{ padding: '8px 12px', fontSize: '12px', color: 'var(--g3)', maxWidth: '260px' }}>
                        {it.kind === 'ptp' && it.promiseDate && (
                          <div><strong>Due:</strong> {new Date(it.promiseDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' })} · {it.promiseStatus || ''}</div>
                        )}
                        {it.kind === 'payment' && it.paymentMode && (
                          <div><strong>{(it.paymentMode || '').toUpperCase()}</strong>{it.transactionRef ? ` · ${it.transactionRef}` : ''}</div>
                        )}
                        {it.kind === 'action' && (it.outcome || it.notes) && (
                          <div title={it.notes || ''}>
                            {it.outcome ? <strong>{it.outcome}</strong> : null}
                            {it.outcome && it.notes ? ' · ' : ''}
                            {it.notes ? (it.notes.length > 80 ? `${it.notes.slice(0, 80)}…` : it.notes) : ''}
                          </div>
                        )}
                        {it.kind === 'action' && it.newStatus && (
                          <div style={{ marginTop: '2px' }}>
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '1px 8px',
                              background: '#e2e8f0',
                              color: '#475569',
                              borderRadius: '999px',
                              fontSize: '10px',
                              fontWeight: 600,
                            }}>
                              <ArrowRightLeft size={10} />
                              {it.previousStatus ? `${statusLabel(it.previousStatus)} → ` : ''}{statusLabel(it.newStatus)}
                            </span>
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: it.kind === 'payment' ? 'var(--green, #16a34a)' : 'var(--g3)' }}>
                        {it.amount ? fmtINR(it.amount) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </OwnerLayout>
  );
}
